(() => {
  if (window.AWMonAtelierPostPurchaseP1?.version) return;

  const VERSION = '1.1.0';
  const BRAND_SESSION_KEY = 'aw_brand_journey_v1';
  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const normalize = (value = '') => String(value)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-');

  const truthy = (value = '') => ['1', 'true', 'yes', 'oui'].includes(String(value).trim().toLowerCase());

  const contextLabels = {
    replica: 'Réplique',
    atelier: 'Prestation Atelier',
    internal: 'Pièce interne',
    mounting: 'Montage / accessoire',
    accessory: 'Accessoire',
    magazine: 'Chargeur',
    power: 'Énergie',
    hpa: 'HPA',
    consumable: 'Consommable',
    equipment: 'Équipement'
  };

  const money = (root, cents) => new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
    style: 'currency',
    currency: root.dataset.currency || window.Shopify?.currency?.active || 'EUR'
  }).format((Number(cents) || 0) / 100);

  const shortDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return new Intl.DateTimeFormat(document.documentElement.lang || 'fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const track = (name, payload = {}) => {
    try { window.AWAnalytics?.track?.(name, payload); } catch (_) {}
  };

  const rememberBrand = (item, source = 'mon_atelier_postpurchase') => {
    if (!item?.brandHandle) return;
    const context = {
      handle: item.brandHandle,
      title: item.brandTitle || '',
      url: item.brandUrl || '',
      source,
      time: Date.now()
    };
    try { window.sessionStorage.setItem(BRAND_SESSION_KEY, JSON.stringify(context)); } catch (_) {}
    try {
      document.dispatchEvent(new CustomEvent('aw:brand-context', { detail: context }));
    } catch (_) {}
  };

  const getOrderSource = (root) => root.querySelector('[data-aw-shopify-orders][data-aw-order-source="enriched"]')
    || root.querySelector('[data-aw-shopify-orders]');

  const parseOrders = (root) => {
    const source = getOrderSource(root);
    if (!source) return [];

    return [...source.querySelectorAll('[data-aw-shopify-order]')].map((orderNode) => {
      const items = [...orderNode.querySelectorAll('[data-aw-shopify-order-item]')].map((node) => {
        const model = node.dataset.model || '';
        const modelHandle = node.dataset.modelHandle || '';
        const replicaId = node.dataset.replicaId || '';
        const modelStatus = node.dataset.modelStatus || '';
        const exact = Boolean(modelHandle || replicaId || model) && !/incompatible|incompatibilite|blocked|bloque/.test(normalize(modelStatus));
        return {
          productId: node.dataset.productId || '',
          variantId: node.dataset.variantId || '',
          productHandle: node.dataset.productHandle || '',
          productUrl: node.dataset.productUrl || '',
          variantTitle: node.dataset.variantTitle || '',
          sku: node.dataset.sku || '',
          quantity: Math.max(1, Number(node.dataset.quantity || 1)),
          title: node.dataset.title || 'Produit AW',
          price: Number(node.dataset.price || 0),
          context: node.dataset.context || 'generic',
          brandHandle: node.dataset.brandHandle || '',
          brandTitle: node.dataset.brandTitle || '',
          brandUrl: node.dataset.brandUrl || '',
          platform: node.dataset.platform || '',
          platformLabel: node.dataset.platformLabel || '',
          manufacturer: node.dataset.manufacturer || '',
          replicaType: node.dataset.replicaType || '',
          configuration: node.dataset.configuration || '',
          workshopRecommended: truthy(node.dataset.workshopRecommended),
          compatibilityScore: node.dataset.compatibilityScore || '',
          compatibilityResult: node.dataset.compatibilityResult || '',
          workshopCertified: truthy(node.dataset.workshopCertified),
          certificationId: node.dataset.certificationId || '',
          certifiedAt: node.dataset.certifiedAt || '',
          replicaId,
          buildId: node.dataset.buildId || '',
          model,
          modelHandle,
          modelStatus,
          replicaContext: node.dataset.replicaContext || '',
          exact
        };
      }).filter((item) => item.productId || item.variantId || item.title);

      return {
        id: orderNode.dataset.orderId || '',
        name: orderNode.dataset.orderName || 'Commande AW',
        date: orderNode.dataset.orderDate || '',
        items
      };
    }).filter((order) => order.items.length > 0);
  };

  const compatibilityUrl = (item, order) => {
    const url = new URL('/pages/compatibilite-airsoft', window.location.origin);
    if (item.modelHandle) url.searchParams.set('aw_model', item.modelHandle);
    if (item.productHandle) url.searchParams.set('aw_product', item.productHandle);
    if (item.brandHandle) url.searchParams.set('aw_brand', item.brandHandle);
    url.searchParams.set('aw_from', 'mon-atelier-order');
    if (order.name) url.searchParams.set('aw_order', order.name.replace(/^#/, ''));
    return `${url.pathname}${url.search}`;
  };

  const atelierUrl = (item, order) => {
    const url = new URL('/pages/notre-atelier-de-preparation-de-repliques-airsoft', window.location.origin);
    if (item.modelHandle) url.searchParams.set('aw_model', item.modelHandle);
    if (item.productHandle) url.searchParams.set('aw_product', item.productHandle);
    if (item.brandHandle) url.searchParams.set('aw_brand', item.brandHandle);
    url.searchParams.set('aw_from', 'mon-atelier-order');
    if (order.name) url.searchParams.set('aw_order', order.name.replace(/^#/, ''));
    return `${url.pathname}${url.search}`;
  };

  const itemCard = (root, order, item) => {
    const typeLabel = contextLabels[item.context] || 'Achat AW';
    const exactLabel = item.exact
      ? 'Modèle exact documenté'
      : item.replicaContext
        ? 'Réplique indiquée à la commande'
        : 'Identification à confirmer';
    const modelLine = item.exact && item.model
      ? `<p class="aw-postpurchase__model"><span>Modèle exact</span><strong>${escapeHtml(item.model)}</strong></p>`
      : item.replicaContext
        ? `<p class="aw-postpurchase__model aw-postpurchase__model--context"><span>Réplique concernée</span><strong>${escapeHtml(item.replicaContext)}</strong></p>`
        : '';
    const refs = [item.sku ? `Réf. ${item.sku}` : '', item.variantTitle && item.variantTitle !== 'Default Title' ? item.variantTitle : ''].filter(Boolean);
    const brandMeta = item.brandHandle && item.brandTitle
      ? `<span>Marque : <a href="${escapeHtml(item.brandUrl || `/blogs/marques-distribuees/${item.brandHandle}`)}" data-aw-postpurchase-action="brand">${escapeHtml(item.brandTitle)}</a></span>`
      : '';
    const productLink = item.productUrl
      ? `<a href="${escapeHtml(item.productUrl)}" data-aw-postpurchase-action="product">Revoir la fiche achetée</a>`
      : '';
    const compatibilityLabel = item.exact ? 'Ouvrir le modèle dans Compatibility' : 'Identifier / vérifier la réplique';
    const compatibilityLink = `<a class="is-primary" href="${escapeHtml(compatibilityUrl(item, order))}" data-aw-postpurchase-action="compatibility">${compatibilityLabel}</a>`;
    const atelierEligible = item.exact || item.context === 'replica' || item.context === 'atelier' || item.workshopCertified;
    const atelierLink = atelierEligible
      ? `<a href="${escapeHtml(atelierUrl(item, order))}" data-aw-postpurchase-action="atelier">Voir l’Atelier AW</a>`
      : '';
    const workshopBadge = item.workshopCertified || item.context === 'atelier'
      ? '<span class="aw-postpurchase__badge aw-postpurchase__badge--atelier">Atelier AW</span>'
      : '';

    return `<article class="aw-postpurchase__item" data-aw-postpurchase-item data-exact="${item.exact ? 'true' : 'false'}" data-product-id="${escapeHtml(item.productId)}" data-variant-id="${escapeHtml(item.variantId)}" data-model-handle="${escapeHtml(item.modelHandle)}" data-platform="${escapeHtml(item.platform)}" data-brand-handle="${escapeHtml(item.brandHandle)}" data-brand-title="${escapeHtml(item.brandTitle)}" data-brand-url="${escapeHtml(item.brandUrl)}">
      <div class="aw-postpurchase__item-head">
        <div>
          <span class="aw-postpurchase__kind">${escapeHtml(typeLabel)}</span>
          <h4>${escapeHtml(item.title)}</h4>
        </div>
        <div class="aw-postpurchase__badges">
          <span class="aw-postpurchase__badge${item.exact ? ' aw-postpurchase__badge--exact' : ''}">${escapeHtml(exactLabel)}</span>
          ${workshopBadge}
        </div>
      </div>
      ${modelLine}
      <div class="aw-postpurchase__meta">
        ${brandMeta}
        ${refs.length ? `<span>${escapeHtml(refs.join(' · '))}</span>` : ''}
        <span>Qté ${item.quantity}</span>
        <strong>${escapeHtml(money(root, item.price))}</strong>
      </div>
      <div class="aw-postpurchase__actions">${compatibilityLink}${atelierLink}${productLink}</div>
    </article>`;
  };

  const orderCard = (root, order) => {
    const exactCount = order.items.filter((item) => item.exact).length;
    const total = order.items.reduce((sum, item) => sum + Number(item.price || 0), 0);
    return `<article class="aw-postpurchase__order" data-aw-postpurchase-order="${escapeHtml(order.id)}">
      <header class="aw-postpurchase__order-head">
        <div>
          <span>COMMANDE SHOPIFY · ${escapeHtml(shortDate(order.date))}</span>
          <h3>${escapeHtml(order.name)}</h3>
        </div>
        <div class="aw-postpurchase__order-proof">
          <strong>${order.items.length}</strong><span>achat${order.items.length > 1 ? 's' : ''} AW</span>
          <strong>${exactCount}</strong><span>modèle${exactCount > 1 ? 's' : ''} exact${exactCount > 1 ? 's' : ''}</span>
          <strong>${escapeHtml(money(root, total))}</strong><span>lignes suivies</span>
        </div>
      </header>
      <div class="aw-postpurchase__items">${order.items.map((item) => itemCard(root, order, item)).join('')}</div>
    </article>`;
  };

  const activatePostPurchase = (root) => {
    if (root.dataset.awPostpurchaseActivated === 'true') return;
    const button = root.querySelector('[data-aw-atelier-tab="recommendations"]');
    const panel = root.querySelector('[data-aw-atelier-panel="recommendations"]');
    if (!button || !panel) return;
    root.querySelectorAll('[data-aw-atelier-tab]').forEach((tab) => {
      const active = tab === button;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    root.querySelectorAll('[data-aw-atelier-panel]').forEach((candidate) => { candidate.hidden = candidate !== panel; });
    root.dataset.awPostpurchaseActivated = 'true';
  };

  const syncStats = (root, orders) => {
    const count = root.querySelector('[data-aw-atelier-history-count]');
    if (count) count.textContent = String(orders.length);
    const article = count?.closest('article');
    const label = article?.querySelector('span');
    const small = article?.querySelector('small');
    if (label) label.textContent = 'Commandes AW';
    if (small) small.textContent = 'commandes Airsoft reliées au compte';
  };

  const render = (root) => {
    const container = root.querySelector('[data-aw-postpurchase-orders]');
    const empty = root.querySelector('[data-aw-postpurchase-empty]');
    if (!container) return false;
    const orders = parseOrders(root);
    if (!orders.length) {
      container.innerHTML = '';
      container.hidden = true;
      if (empty) empty.hidden = false;
      return false;
    }

    container.innerHTML = orders.slice(0, 12).map((order) => orderCard(root, order)).join('');
    container.hidden = false;
    if (empty) empty.hidden = true;
    root.dataset.awPostpurchaseOrderCount = String(orders.length);
    syncStats(root, orders);
    activatePostPurchase(root);

    if (root.dataset.awPostpurchaseTracked !== 'true') {
      root.dataset.awPostpurchaseTracked = 'true';
      const brandHandles = new Set(orders.flatMap((order) => order.items.map((item) => item.brandHandle).filter(Boolean)));
      track('mon_atelier_postpurchase_ready', {
        order_count: orders.length,
        item_count: orders.reduce((sum, order) => sum + order.items.length, 0),
        exact_item_count: orders.reduce((sum, order) => sum + order.items.filter((item) => item.exact).length, 0),
        brand_count: brandHandles.size
      });
    }
    return true;
  };

  const init = (root) => {
    if (!root || root.dataset.awPostpurchaseBound === 'true') return;
    root.dataset.awPostpurchaseBound = 'true';

    const refresh = () => render(root);
    refresh();
    window.setTimeout(refresh, 80);
    window.setTimeout(refresh, 400);

    const observer = new MutationObserver(() => {
      if (getOrderSource(root)) refresh();
    });
    observer.observe(root, { childList: true, subtree: false });

    root.addEventListener('click', (event) => {
      const action = event.target.closest('[data-aw-postpurchase-action]');
      if (!action) return;
      const itemNode = action.closest('[data-aw-postpurchase-item]');
      const item = itemNode ? {
        productId: itemNode.dataset.productId || '',
        variantId: itemNode.dataset.variantId || '',
        modelHandle: itemNode.dataset.modelHandle || '',
        platform: itemNode.dataset.platform || '',
        brandHandle: itemNode.dataset.brandHandle || '',
        brandTitle: itemNode.dataset.brandTitle || '',
        brandUrl: itemNode.dataset.brandUrl || ''
      } : null;
      if (item?.brandHandle) rememberBrand(item, `mon_atelier_${action.dataset.awPostpurchaseAction || 'link'}`);
      track('mon_atelier_postpurchase_action', {
        action: action.dataset.awPostpurchaseAction || 'link',
        exact: itemNode?.dataset.exact === 'true',
        product_id: item?.productId || '',
        variant_id: item?.variantId || '',
        model_handle: item?.modelHandle || '',
        platform: item?.platform || '',
        brand_handle: item?.brandHandle || '',
        brand_title: item?.brandTitle || ''
      });
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-aw-atelier]').forEach(init);
  window.AWMonAtelierPostPurchaseP1 = { version: VERSION, initAll };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
